/**
 * Components that render configuration options/settings for each
 * trigger event type.
 *
 * Note: Styling for these components is based on
 * that for CreateTriggerDrawer
 */
import {
  constFunction,
  opArtifactId,
  opArtifactName,
  opDict,
  opMap,
} from '@wandb/weave-ui/cg';
import {useNodeValue} from '@wandb/weave-ui/cgreact';
import React, {useEffect, useState} from 'react';

import {
  TriggerScopeType,
  useProjectArtifactCollectionsQuery,
} from '../../../generated/graphql';
import {getArtifactPortfoliosNode} from '../../../pages/EntityRegistryPage';
import {Key as ArtifactKey} from '../../../util/artifacts';
import {
  EMPTY_FILTER_VALUE,
  FilterKey,
  ValueFilter,
} from '../../../util/filters';
import {Select} from '../../Form/Select';
import {IconClose} from '../../Icon';
import {Tooltip} from '../../Tooltip';
import * as S from './TriggerEventConfiguration.styles';
import {OptionType} from './utils';

export type TriggerRootFilter = {
  op: 'OR';
  filters: [
    {
      op: 'AND';
      filters: Array<ValueFilter<FilterKey>>;
    }
  ];
};

type EventConfigurationFilterProps = {
  descriptionString: string;
  index: number;
  filter: ValueFilter<FilterKey>;
  projectName: string;
  entityName: string;
  triggerScopeID?: string;
  setFilter(index: number, filter: ValueFilter<FilterKey>): void;
  setTriggerScopeID(val: string): void;
  setTriggerArtifactCollectionName?(val: string): void;
};

type LinkModelFilterConfigurerProps = {
  index: number;
  filter: ValueFilter<FilterKey>;
  projectName: string;
  entityName: string;
  triggerScopeID?: string;
  setTriggerScopeID(val: string): void;
  setFilter(index: number, filter: ValueFilter<FilterKey>): void;
  setTriggerArtifactCollectionName?(val: string): void;
};

export type EventConfigurationProps = {
  filters: TriggerRootFilter['filters'][0];
  projectName: string;
  entityName: string;
  projectID: string;
  artifactCollectionID?: string;
  triggerScopeType?: TriggerScopeType;
  triggerScopeID?: string;
  setFilter(i: number, filter: ValueFilter<FilterKey>): void;
  pushFilter(newFilter: ValueFilter<FilterKey>): void;
  deleteFilter(index: number): void;
  setTriggerScopeType?(val: TriggerScopeType): void;
  setTriggerScopeID(val: string): void;
  setTriggerArtifactCollectionName?(val: string): void;
};

export const ArtifactAliasFilterCreator = ({
  filters: rootFilters,
  projectName,
  entityName,
  projectID,
  artifactCollectionID,
  setFilter,
  deleteFilter,
  setTriggerScopeType,
  setTriggerScopeID,
  triggerScopeType,
  triggerScopeID,
  setTriggerArtifactCollectionName,
}: EventConfigurationProps) => {
  const [pushed, setPushed] = useState(
    triggerScopeType === TriggerScopeType.ArtifactCollection
  );
  useEffect(() => {
    if (rootFilters.filters[0] && rootFilters.filters[0]?.value !== '') {
      if (setTriggerScopeType != null) {
        if (!pushed) {
          setTriggerScopeType(TriggerScopeType.Project);
          setTriggerScopeID(projectID);
        } else {
          setTriggerScopeType(TriggerScopeType.ArtifactCollection);
        }
      }
    } else {
      setPushed(false);
      setTriggerScopeID('');
    }
  }, [
    pushed,
    projectID,
    setTriggerScopeType,
    setTriggerScopeID,
    rootFilters.filters,
  ]);
  return (
    <>
      <S.LabelRow>
        <S.Label>Alias regex</S.Label>
      </S.LabelRow>
      <S.StyledTextField
        placeholder="Name or regex"
        value={(rootFilters.filters[0]?.value as string) ?? ''}
        onChange={value => {
          setFilter(0, {
            key: {section: 'artifact', name: 'alias'},
            op: 'REGEX',
            value: value as string,
          });
        }}
        autoFocus={true}
      />
      {!pushed && rootFilters.filters[0] && (
        <S.AddFilter
          className="fake-link filter__new"
          data-test="filter-add"
          onClick={() => {
            setPushed(true);
            if (setTriggerScopeType != null) {
              setTriggerScopeType(TriggerScopeType.ArtifactCollection);
              setTriggerScopeID(artifactCollectionID ?? '');
            }
          }}>
          Add collection filter
        </S.AddFilter>
      )}
      {pushed && (
        <>
          <S.LabelRow>
            <S.Label>Artifact collection</S.Label>
          </S.LabelRow>
          <S.InlineBlockAction>
            <S.InlineBlockActionLeft>
              <ArtifactCollectionSelector
                index={1}
                descriptionString="Artifact collection"
                entityName={entityName}
                projectName={projectName}
                filter={rootFilters.filters[1] ?? EMPTY_FILTER_VALUE}
                setFilter={(index: number, f: ValueFilter<ArtifactKey>) => {
                  setFilter(index, f);
                }}
                setTriggerScopeID={setTriggerScopeID}
                triggerScopeID={triggerScopeID}
                setTriggerArtifactCollectionName={
                  setTriggerArtifactCollectionName
                }
              />
            </S.InlineBlockActionLeft>
            <S.InlineBlockActionRight>
              <Tooltip
                inverted
                size="mini"
                content="Remove filter"
                position="right center"
                on="hover"
                trigger={
                  <S.DeleteFilter>
                    <IconClose
                      data-test="filter-delete"
                      onClick={() => {
                        setPushed(false);
                        setTriggerScopeID(projectID);
                        if (setTriggerScopeType != null) {
                          setTriggerScopeType(TriggerScopeType.Project);
                          if (setTriggerArtifactCollectionName) {
                            setTriggerArtifactCollectionName('');
                          }
                        }

                        deleteFilter(1);
                      }}
                    />
                  </S.DeleteFilter>
                }
              />
            </S.InlineBlockActionRight>
          </S.InlineBlockAction>
        </>
      )}
    </>
  );
};

export const LinkModelFilterCreator = (props: EventConfigurationProps) => {
  return (
    <LinkModelFilterConfigurer
      {...props}
      index={0}
      setFilter={props.setFilter}
      filter={props.filters.filters[0]}
    />
  );
};

type artifactPortfolioInfo = {
  id: string;
  name: string;
};

const LinkModelFilterConfigurer = (props: LinkModelFilterConfigurerProps) => {
  const portfoliosNode = React.useMemo(
    () => getArtifactPortfoliosNode('model', props.entityName),
    [props.entityName]
  );

  const portfoliosDictNode = opMap({
    arr: portfoliosNode,
    mapFn: constFunction({row: 'artifact'}, ({row}) => {
      return opDict({
        id: opArtifactId({artifact: row}),
        name: opArtifactName({artifact: row}),
      } as any);
    }),
  });
  const p = useNodeValue(portfoliosDictNode);

  const options: OptionType[] = p?.result?.map(
    (port: artifactPortfolioInfo) => {
      return {
        value: port.id,
        label: port.name,
      };
    }
  );
  useEffect(() => {
    if (props.setTriggerArtifactCollectionName && props.triggerScopeID) {
      props.setTriggerArtifactCollectionName(
        options?.find(option => option.value === props.triggerScopeID)?.label ??
          ''
      );
    }
  });

  if (p.loading) {
    return null;
  }

  if (options.length === 0) {
    return (
      <S.Label>
        No portfolios available, create a model registry to use this event.
      </S.Label>
    );
  }
  return (
    <>
      <S.LabelRow>
        <S.Label>Registered model</S.Label>
      </S.LabelRow>
      <Select<OptionType | null>
        defaultValue={
          props.triggerScopeID
            ? {
                label:
                  options.find(opt => opt.value === props.triggerScopeID)
                    ?.label ?? '',
                value: props.triggerScopeID,
              }
            : null
        }
        options={options}
        onChange={(option: OptionType | null) => {
          if (option) {
            props.setTriggerScopeID(option.value);
            if (props.setTriggerArtifactCollectionName) {
              props.setTriggerArtifactCollectionName(option.label);
            }
          }
        }}
      />
    </>
  );
};

export const ArtifactCollectionFilterCreator = (
  props: EventConfigurationProps
) => {
  return (
    <>
      <S.LabelRow>
        <S.Label>Artifact collection</S.Label>
      </S.LabelRow>
      <ArtifactCollectionSelector
        {...props}
        index={0}
        setFilter={props.setFilter}
        filter={props.filters.filters[0]}
        descriptionString="Artifact collection"
        triggerScopeID={props.triggerScopeID}
        setTriggerScopeID={props.setTriggerScopeID}
        setTriggerArtifactCollectionName={
          props.setTriggerArtifactCollectionName
        }
      />
    </>
  );
};

export const ArtifactCollectionSelector = (
  props: EventConfigurationFilterProps
) => {
  const {data, loading} = useProjectArtifactCollectionsQuery({
    variables: {entityName: props.entityName, projectName: props.projectName},
  });
  const setTriggerScopeID = props.setTriggerScopeID;
  const setTriggerArtifactCollectionName =
    props.setTriggerArtifactCollectionName;

  const artifactCollections: OptionType[] =
    data?.project?.artifactTypes.edges
      .map(artifactTypeEdge => {
        return artifactTypeEdge.node?.artifactCollections?.edges.map(
          artifactCollectionEdge => {
            return {
              label: artifactCollectionEdge.node?.name as string,
              value: artifactCollectionEdge.node?.id as string,
            };
          }
        );
      })
      .flat()
      .filter((v): v is OptionType => v != null) ?? [];

  useEffect(() => {
    if (setTriggerArtifactCollectionName && props.triggerScopeID) {
      setTriggerArtifactCollectionName(
        artifactCollections.find(
          option => option.value === props.triggerScopeID
        )?.label ?? ''
      );
    }
  });
  if (loading || data == null) {
    return <div></div>;
  }
  return (
    <Select<OptionType | null>
      placeholder={`Select your ${props.descriptionString.toLowerCase()}`}
      defaultValue={
        props.triggerScopeID
          ? {
              label:
                artifactCollections.find(
                  ac => ac.value === props.triggerScopeID
                )?.label ?? '',
              value: props.triggerScopeID,
            }
          : null
      }
      options={artifactCollections}
      onChange={(option: OptionType | null) => {
        if (option) {
          setTriggerScopeID(option.value);
          if (setTriggerArtifactCollectionName) {
            setTriggerArtifactCollectionName(option.label);
          }
        }
      }}
    />
  );
};
